import apiClient from './api';

// Types
export interface WaitlistQuestion {
  id: string;
  service_id: string;
  question: string;
  question_type: 'text' | 'multiple_choice' | 'single_choice';
  options: Array<{
    id: string;
    text: string;
  }>;
  required: boolean;
  display_order: number;
}

export interface WaitlistEntry {
  id: string;
  service_id: string;
  created_at: string;
  upcoming_services: {
    id: string;
    name: string;
    description: string;
    icon?: string;
    launch_date: string;
  };
}

export interface WaitlistServiceData {
  id: string;
  name: string;
}

export interface SurveyResponse {
  questionId: string;
  response: any;
}

export interface WaitlistQuestionResponse {
  service: WaitlistServiceData;
  questions: WaitlistQuestion[];
  userResponses: Array<{
    question_id: string;
    response: any;
  }>;
  hasJoinedWaitlist: boolean;
}

export interface JoinWaitlistRequest {
  serviceId: string;
  surveyResponses: SurveyResponse[];
}

export interface JoinWaitlistResponse {
  success: boolean;
  message: string;
  waitlistEntryId: string;
}

// Service
export const waitlistService = {
  /**
   * Get all waitlist entries for the current user
   */
  getWaitlistEntries: async (): Promise<WaitlistEntry[]> => {
    try {
      const response = await apiClient.get<{ waitlistEntries: WaitlistEntry[] }>('/waitlist');
      return response.data.waitlistEntries;
    } catch (error) {
      console.error('Error fetching waitlist entries:', error);
      throw error;
    }
  },

  /**
   * Check if the user has already joined the waitlist for a service
   */
  checkWaitlistStatus: async (serviceId: string): Promise<{ hasJoined: boolean, entry?: WaitlistEntry }> => {
    try {
      const response = await apiClient.get<{ waitlistEntry: WaitlistEntry | null }>(`/waitlist?serviceId=${serviceId}`);
      return {
        hasJoined: !!response.data.waitlistEntry,
        entry: response.data.waitlistEntry || undefined
      };
    } catch (error) {
      console.error(`Error checking waitlist status for service ${serviceId}:`, error);
      throw error;
    }
  },

  /**
   * Get survey questions for a service
   */
  getSurveyQuestions: async (serviceId: string): Promise<WaitlistQuestionResponse> => {
    try {
      const response = await apiClient.get<WaitlistQuestionResponse>(`/waitlist/questions?serviceId=${serviceId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching survey questions for service ${serviceId}:`, error);
      throw error;
    }
  },

  /**
   * Join the waitlist for a service
   */
  joinWaitlist: async (request: JoinWaitlistRequest): Promise<JoinWaitlistResponse> => {
    try {
      const response = await apiClient.post<JoinWaitlistResponse>('/waitlist', request);
      return response.data;
    } catch (error) {
      console.error(`Error joining waitlist for service ${request.serviceId}:`, error);
      throw error;
    }
  }
}; 